import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * Hledani_pismen (Letter Search) - Find letters in a grid
 *
 * Layout: Multiple portrait pages, 2×5 grid per page.
 * Each cell: image (top-right), label text (top-left), 10×3 letter grid (bottom).
 * Label letters are placed randomly in the grid, rest filled with random letters.
 *
 * Migrated from C# EduFrame.Img10.Generators.Hledani_pismen
 */
export class HledaniPismen implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const itemsPerPage = 10;
    const pagesNeeded = Math.ceil(opts.list.length / itemsPerPage);
    const pages: PageGen[] = [];
    for (let i = 0; i < pagesNeeded; i++) {
      pages.push(pdf.addPage());
    }

    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(2, 5, margin, PdfSize.A4);
    const bgBrush = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1));
    const {random} = opts.data;

    for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
      const page = pages[pageIndex];
      const pageStartIndex = pageIndex * itemsPerPage;

      for (let x = 0; x < 2; x++) {
        for (let y = 0; y < 5; y++) {
          const index = pageStartIndex + x * 5 + y;
          if (index >= opts.list.length) continue;

          const rect = new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          );
          const imageRect = new PdfRect(
            rect.x + rect.width * 0.7,
            rect.y,
            rect.width * 0.3,
            rect.height * 0.3
          );
          const stripRect = new PdfRect(
            rect.x,
            rect.y + rect.height * 0.05,
            rect.width,
            rect.height * 0.2
          );
          const textRect = new PdfRect(
            stripRect.left + stripRect.width * 0.05,
            stripRect.y,
            stripRect.width * 0.65,
            stripRect.height
          );
          const charRect = new PdfRect(
            rect.left + rect.width * 0.05,
            rect.y + rect.height * 0.35,
            rect.width * 0.9,
            rect.height * 0.6
          );

          const label = opts.list[index].text.toUpperCase().replace(/\s/g, '');
          const f = page.adjustFont(textRect.width, [label], GUtil.baseFont);

          const countX = 10;
          const countY = 3;

          const missing: string[] = [];
          for (let ci = 0; ci < 26; ci++) {
            const ch = String.fromCharCode(65 + ci);
            if (!label.includes(ch) && ch !== 'Q' && ch !== 'W' && ch !== 'X') {
              missing.push(ch);
            }
          }

          const positions = Array.from({ length: countX * countY }, (_, i) => i);
          for (let i = positions.length - 1; i > 0; i--) {
            const j = random.next(0, i + 1);
            [positions[i], positions[j]] = [positions[j], positions[i]];
          }
          const labelPositions = positions.slice(0, label.length).sort((a, b) => a - b);

          const grid = new Map<number, string>();
          for (let i = 0; i < labelPositions.length; i++) {
            grid.set(labelPositions[i], label[i]);
          }

          for (let i = 0; i < countX * countY; i++) {
            if (!grid.has(i)) {
              grid.set(i, missing[random.next(0, missing.length)]);
            }
          }

          page.drawRectangle(null, bgBrush, stripRect);
          page.drawLabel(opts.list[index].label, { rect: imageRect });
          page.drawString(label, f, PdfBrush.Black, null, textRect, ETextAlign.CenterLeft);
          page.drawRectangle(GUtil.cutPen, null, rect);

          for (let x1 = 0; x1 < countX; x1++) {
            for (let y1 = 0; y1 < countY; y1++) {
              const ii = y1 * countX + x1;
              const r = new PdfRect(
                charRect.x + (x1 * charRect.width) / countX,
                charRect.y + (y1 * charRect.height) / countY,
                charRect.width / countX,
                charRect.height / countY
              );
              page.drawString(
                grid.get(ii)!,
                GUtil.baseFont,
                PdfBrush.Black,
                null,
                r,
                ETextAlign.Center
              );
            }
          }
        }
      }
    }
  }
}
